/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Option,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
    Tree,
    TreeItem,
    TreeItemLayout,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Add24Regular, ChevronDown24Regular, ChevronRight24Regular, Edit24Regular, Eye24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArbeitspaketItem } from '../../models/ArbeitspaketItem';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    toolbar: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        alignItems: 'center',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
        flex: 1,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    childRow: {
        backgroundColor: tokens.colorNeutralBackground2,
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    formLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    errorMessage: {
        color: tokens.colorStatusDangerForeground1,
        marginBottom: tokens.spacingVerticalL,
    },
    colExpand: {
        width: '45px',
        minWidth: '45px',
    },
    colActions: {
        width: '55px',
        minWidth: '55px',
    },
    colPackageNumber: {
        width: '150px',
        minWidth: '150px',
    },
    colStartDate: {
        width: '100px',
        minWidth: '100px',
    },
    colEndDate: {
        width: '100px',
        minWidth: '100px',
    },
    sectionHeader: {
        fontWeight: 700,
        fontSize: tokens.fontSizeBase400,
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorBrandForeground1,
    },
    sectionDescription: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalM,
    },
    readOnlyField: {
        padding: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        fontWeight: 500,
    },


});

interface ProjektmanagementArbeitspaketeProps {
    project: ProjektItem | null;
}

interface ArbeitspaketForm extends Omit<ArbeitspaketItem, 'datumVon' | 'datumBis'> {
    startdatum: Date | null;
    endedatum: Date | null;
    createMilestone?: boolean;
}

const ProjektmanagementArbeitspakete: React.FC<ProjektmanagementArbeitspaketeProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [arbeitspakete, setArbeitspakete] = useState<ArbeitspaketItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
    const [ebenen, setEbenen] = useState<ArbeitspaketItem[]>([]);
    const [editedEbenen, setEditedEbenen] = useState<ArbeitspaketItem[]>([]);
    const [isLoadingEbenen, setIsLoadingEbenen] = useState(false);
    const [isSavingEbenen, setIsSavingEbenen] = useState(false);

    // Mitarbeiter search state
    const [availableMitarbeiter, setAvailableMitarbeiter] = useState<any[]>([]);
    const [memberSearchResults, setMemberSearchResults] = useState<any[]>([]);
    const [mitarbeiterLoaded, setMitarbeiterLoaded] = useState(false);
    const [responsibilitySearchTimeout, setResponsibilitySearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [isResponsibilitySearching, setIsResponsibilitySearching] = useState(false);

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState<ArbeitspaketItem | null>(null);
    const [dragOverItem, setDragOverItem] = useState<ArbeitspaketItem | null>(null);
    const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'child'>('after');
    const [isSavingDragDrop, setIsSavingDragDrop] = useState(false);

    const [formData, setFormData] = useState<ArbeitspaketForm>({
        paketID: 0,
        projektID: project?.projektID || 0,
        prefix: '',
        bezeichnung: '',
        startdatum: null,
        endedatum: null,
        verantwortung: '',
        personalKostenPlan: 0,
        parentID: undefined,
        orderBy: 0,
        createMilestone: false,
        userID: undefined,
    } as any);

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch work package levels (prefixes)
    const fetchEbenen = useCallback(async () => {
        if (!project?.projektID || !dataService) return;

        try {
            setIsLoadingEbenen(true);
            const data = await dataService.getArbeitspaketEbenenListe(project.projektID);
            setEbenen(data);
            setEditedEbenen(data);
        } catch (err) {
            console.error('Error loading work package levels:', err);
            setError('Error loading work package levels');
        } finally {
            setIsLoadingEbenen(false);
        }
    }, [project?.projektID, dataService]);

    // Save work package levels
    const handleSaveEbenen = async () => {
        if (!dataService || !project?.projektID) return;

        try {
            setIsSavingEbenen(true);
            await dataService.updateArbeitspaketEbenen(project.projektID, editedEbenen);
            setEbenen(editedEbenen);
            setError(null);
            // Refetch work packages because prefix changes affect package numbers
            await fetchArbeitspakete();
        } catch (err) {
            console.error('Error saving work package levels:', err);
            setError('Error saving work package levels');
        } finally {
            setIsSavingEbenen(false);
        }
    };

    // Fetch work package levels on component mount
    const fetchArbeitspakete = useCallback(async () => {
        if (!project?.projektID || !dataService) return;
        console.log('DataService instance:', dataService);
        try {
            setIsLoading(true);
            setError(null);
            console.log('Fetching work packages for project ID:', project.projektID);
            const data = await dataService.getArbeitspaketListe(project.projektID);
            console.log('Arbeitspakete', data);
            setArbeitspakete(data);
        } catch (err) {
            console.error('Error loading work packages:', err);
            setError('Error loading work packages');
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchArbeitspakete();
        fetchEbenen();
    }, [fetchArbeitspakete, fetchEbenen]);

    // Group work packages hierarchically
    const hierarchicalData = useMemo(() => {
        const groupedMap = new Map<number | null, ArbeitspaketItem[]>();

        // Group by parent ID
        arbeitspakete.forEach(item => {
            const parentId = item.parentID || null;
            if (!groupedMap.has(parentId)) {
                groupedMap.set(parentId, []);
            }
            groupedMap.get(parentId)!.push(item);
        });

        // Sort each group by orderBy
        groupedMap.forEach((items) => {
            items.sort((a, b) => (a.orderBy || 0) - (b.orderBy || 0));
        });

        return groupedMap;
    }, [arbeitspakete]);



    // Get top-level (no parent) work packages
    const topLevelPackages = useMemo(() => {
        return hierarchicalData.get(null) || [];
    }, [hierarchicalData]);

    // Get parent options (only top-level for "Übergeordnetes Arbeitspaket")
    const parentOptions = useMemo(() => {
        return topLevelPackages.map(item => ({
            label: `${item.prefixTitle}: ${item.bezeichnung}`,
            value: item.paketID.toString(),
        }));
    }, [topLevelPackages]);

    // Get "insert after" options - show siblings when editing, top-level when creating
    const insertAfterOptions = useMemo(() => {
        if (editingId) {
            // When editing: show siblings (same parent)
            const currentItem = arbeitspakete.find(ap => ap.paketID === editingId);
            if (!currentItem) return [];

            const siblings = hierarchicalData.get(currentItem.parentID || null) || [];
            return siblings.map(item => ({
                label: `${item.prefixTitle}: ${item.bezeichnung}`,
                value: item.paketID.toString(),
            }));
        } else {
            // When creating: show siblings of selected parent (or top-level if no parent selected)
            const siblings = hierarchicalData.get(formData.parentID || null) || [];
            const options = [
                {
                    label: '↑ At beginning',
                    value: '-2',
                },
                ...siblings.map(item => ({
                    label: `↓ After: ${item.prefixTitle}: ${item.bezeichnung}`,
                    value: item.paketID.toString(),
                })),
            ];
            return options;
        }
    }, [editingId, arbeitspakete, hierarchicalData, formData.parentID]);

    const renderTreeNode = (item: ArbeitspaketItem): React.ReactNode => {
        const children = hierarchicalData.get(item.paketID) || [];
        const isSelected = formData.parentID === item.paketID;

        return (
            <TreeItem
                key={item.paketID}
                itemType={children.length > 0 ? 'branch' : 'leaf'}
            >
                <TreeItemLayout
                    onClick={() => {
                        setFormData({
                            ...formData,
                            parentID: item.paketID,
                        });
                    }}
                    style={{
                        fontWeight: isSelected ? 'bold' : 'normal',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        borderRadius: '2px',
                        border: isSelected ? `1px solid ${tokens.colorBrandStroke1}` : 'none',
                    }}
                >
                    {item.prefixTitle}: {item.bezeichnung}
                </TreeItemLayout>
                {children.length > 0 && (
                    <Tree>
                        {children.map(child => renderTreeNode(child))}
                    </Tree>
                )}
            </TreeItem>
        );
    };

    const renderParentTree = (): React.ReactNode => {
        const topLevel = hierarchicalData.get(null) || [];
        // When selecting "None", the last sibling will be the last top-level item
        const lastTopLevelItem = topLevel[topLevel.length - 1];
        const lastTopLevelId = lastTopLevelItem?.paketID;

        return (
            <Tree
                style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                }}
            >
                <TreeItem itemType="leaf">
                    <TreeItemLayout
                        onClick={() => {
                            setFormData({
                                ...formData,
                                parentID: undefined,
                                orderBy: lastTopLevelId || 0,
                            });
                        }}
                        style={{
                            fontWeight: formData.parentID === undefined ? 'bold' : 'normal',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '2px',
                            border: formData.parentID === undefined ? `1px solid ${tokens.colorBrandStroke1}` : 'none',
                            padding: '4px 8px',
                        }}
                    >
                        None
                    </TreeItemLayout>
                </TreeItem>
                {topLevel.map(item => renderTreeNode(item))}
            </Tree>
        );
    };

    // Load all available mitarbeiter for responsibility search
    const loadAvailableMitarbeiter = useCallback(async () => {
        if (!dataService || mitarbeiterLoaded) {
            return;
        }

        try {
            const mitarbeiter = await dataService.getMitarbeiter();
            setAvailableMitarbeiter(mitarbeiter);
            setMitarbeiterLoaded(true);
        } catch (error) {
            console.error('Error loading mitarbeiter:', error);
            setAvailableMitarbeiter([]);
        }
    }, [dataService, mitarbeiterLoaded]);

    // Handle responsibility member search
    const handleResponsibilitySearch = useCallback(
        (searchTerm: string) => {
            if (responsibilitySearchTimeout) {
                clearTimeout(responsibilitySearchTimeout);
            }

            if (searchTerm.length >= 2) {
                const timeout = setTimeout(() => {
                    try {
                        // Filter available mitarbeiter based on search term
                        const results = availableMitarbeiter.filter(
                            (member) =>
                                `${member.vorname} ${member.nachname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                member.vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                member.nachname?.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        setMemberSearchResults(results);
                    } catch (error) {
                        console.error('Error searching members:', error);
                        setMemberSearchResults([]);
                    }
                }, 300);
                setResponsibilitySearchTimeout(timeout);
            } else {
                setMemberSearchResults([]);
            }
        },
        [availableMitarbeiter, responsibilitySearchTimeout],
    );

    const handleAddNew = () => {
        setEditingId(null);
        setIsViewOnly(false);
        loadAvailableMitarbeiter();
        setMemberSearchResults([]);
        setFormData({
            paketID: 0,
            projektID: project?.projektID || 0,
            prefix: '',
            bezeichnung: '',
            startdatum: null,
            endedatum: null,
            verantwortung: '',
            personalkostenPlan: 0,
            parentID: undefined,
            orderBy: -2,
            createMilestone: false,
            userID: undefined,
        } as any);
        setIsResponsibilitySearching(false);
        setIsDrawerOpen(true);
    };

    const handleView = (item: ArbeitspaketItem) => {
        setEditingId(item.paketID);
        setIsViewOnly(true);
        setIsResponsibilitySearching(false);
        loadAvailableMitarbeiter();
        setMemberSearchResults([]);
        setFormData({
            ...item,
            startdatum: item.datumVon ? new Date(item.datumVon) : null,
            endedatum: item.datumBis ? new Date(item.datumBis) : null,
            createMilestone: false,
            userID: item.userID,
            userFullName: item.userID ? item.userFullName : '', // Clear userFullName if no userID
        } as any);
        setIsDrawerOpen(true);
    };

    const handleEdit = (item: ArbeitspaketItem) => {
        setEditingId(item.paketID);
        setIsViewOnly(false);
        setIsResponsibilitySearching(false);
        loadAvailableMitarbeiter();
        setMemberSearchResults([]);
        setFormData({
            ...item,
            startdatum: item.datumVon ? new Date(item.datumVon) : null,
            endedatum: item.datumBis ? new Date(item.datumBis) : null,
            createMilestone: false,
            userID: item.userID,
            userFullName: item.userID ? item.userFullName : '', // Clear userFullName if no userID
        } as any);
        setIsDrawerOpen(true);
    };

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, item: ArbeitspaketItem) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'move';
        if (e.currentTarget) {
            e.currentTarget.style.opacity = '0.5';
        }
    };

    const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
        if (e.currentTarget) {
            e.currentTarget.style.opacity = '1';
        }
        setDragOverItem(null);
        setDropPosition('after');
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, item: ArbeitspaketItem) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        setDragOverItem(item);

        // Determine drop position based on mouse Y position within the row
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;

        if (y < height * 0.33) {
            setDropPosition('before');
        } else if (y > height * 0.66) {
            setDropPosition('after');
        } else {
            setDropPosition('child');
        }
    };

    const handleDragLeave = () => {
        setDragOverItem(null);
        setDropPosition('after');
    };

    const handleDrop = async (e: React.DragEvent<HTMLTableRowElement>, targetItem: ArbeitspaketItem) => {
        e.preventDefault();

        if (!draggedItem || draggedItem.paketID === targetItem.paketID || !dataService) {
            setDragOverItem(null);
            return;
        }

        try {
            setIsSavingDragDrop(true);

            // Helper function to convert Date to ISO date string
            const dateToISOString = (date: Date | null): string => {
                if (!date) return '0001-01-01';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Helper function to check if a package is a descendant of another
            const isDescendant = (possibleChild: ArbeitspaketItem, possibleParent: ArbeitspaketItem): boolean => {
                let current: ArbeitspaketItem | undefined = possibleChild;
                while (current) {
                    if (current.paketID === possibleParent.paketID) return true;
                    if (current !== undefined && (current.parentID === null || current.parentID === undefined)) break;
                    current = arbeitspakete.find(ap => current !== undefined && ap.paketID === current.parentID);
                }
                return false;
            };

            let newParentID: number | undefined = draggedItem.parentID;
            let insertAfterId = -1;
            let newHierarchyLevel = 1;

            if (dropPosition === 'child') {
                // Prevent item from becoming its own parent
                if (targetItem.paketID === draggedItem.paketID) {
                    setDragOverItem(null);
                    return;
                }

                // Prevent circular reference: don't make item a child of its own descendants
                if (isDescendant(targetItem, draggedItem)) {
                    console.warn('Cannot make item a child of its own descendant');
                    setDragOverItem(null);
                    return;
                }

                // Drop to become a child of target item
                newParentID = targetItem.paketID;
                newHierarchyLevel = getTargetHierarchyLevel(targetItem) + 1;

                // Find the last child of the target to insert after
                const targetChildren = arbeitspakete.filter(ap => ap.parentID === targetItem.paketID);
                if (targetChildren.length > 0) {
                    // Insert after the last existing child
                    const lastChild = targetChildren.sort((a, b) => b.orderBy - a.orderBy)[0];
                    insertAfterId = lastChild.paketID;
                } else {
                    // No children yet - insert as first child using -2
                    insertAfterId = -2;
                }
            } else {
                // Drop as sibling (before or after)
                newParentID = targetItem.parentID;
                newHierarchyLevel = getTargetHierarchyLevel(targetItem);

                if (dropPosition === 'before') {
                    // Insert before target - need to find the item before target
                    const siblings = hierarchicalData.get(targetItem.parentID || null) || [];
                    const targetIndex = siblings.findIndex(s => s.paketID === targetItem.paketID);

                    if (targetIndex > 0) {
                        // Insert after the previous sibling
                        insertAfterId = siblings[targetIndex - 1].paketID;
                    } else {
                        // Target is first, so insert at beginning (-2 tells API to insert at position 1)
                        insertAfterId = -2;
                    }
                } else {
                    // Drop after target
                    insertAfterId = targetItem.paketID;
                }
            }

            // Determine the correct prefix for the new hierarchy level
            let newPrefix = draggedItem.prefix;
            if (newHierarchyLevel > 0 && newHierarchyLevel <= ebenen.length) {
                const levelPrefix = ebenen[newHierarchyLevel - 1]?.prefix;
                if (levelPrefix) {
                    newPrefix = levelPrefix;
                }
            }

            // Helper function to get hierarchy level of a target item
            function getTargetHierarchyLevel(item: ArbeitspaketItem): number {
                let level = 1;
                let current: ArbeitspaketItem | undefined = item;
                while (current?.parentID) {
                    level++;
                    current = arbeitspakete.find(ap => ap.paketID === current?.parentID);
                }
                return level;
            }

            // Prepare submit data - don't modify orderBy, let the API handle it
            const submitData: ArbeitspaketItem = {
                paketID: draggedItem.paketID,
                projektID: project?.projektID || 0,
                prefix: newPrefix,
                datumVon: dateToISOString(new Date(draggedItem.datumVon)) as any,
                datumBis: dateToISOString(new Date(draggedItem.datumBis)) as any,
                userFullName: draggedItem.userFullName,
                userID: draggedItem.userID,
                personalkostenPlan: draggedItem.personalkostenPlan,
                parentID: newParentID,
                orderBy: -1, // Let API calculate this
            } as any;

            // API will handle reordering based on insertAfterId
            await dataService.updateArbeitspaket(submitData, insertAfterId);
            await fetchArbeitspakete();

            // Also refresh the levels in case hierarchy depth changed
            if (project?.projektID) {
                const ebenenData = await dataService.getArbeitspaketEbenenListe(project.projektID);
                setEbenen(ebenenData);
                setEditedEbenen(JSON.parse(JSON.stringify(ebenenData)));
            }

            setError(null);
        } catch (err) {
            console.error('Error updating work package order:', err);
            setError('Error updating work package order');
        } finally {
            setIsSavingDragDrop(false);
            setDragOverItem(null);
            setDropPosition('after');
        }
    };

    // Check if form is valid
    const isFormValid = useMemo(() => {
        // Common validation for both add and edit modes
        if (!formData.bezeichnung || formData.bezeichnung.trim() === '') {
            return false; // Description is required
        }
        if (formData.personalkostenPlan === undefined || formData.personalkostenPlan === null || isNaN(formData.personalkostenPlan) || formData.personalkostenPlan < 0) {
            return false; // Personnel costs must be a valid number (including 0)
        }
        if (formData.createMilestone === undefined) {
            return false; // Create milestone must be selected
        }

        // Add mode specific validation
        if (!editingId) {
            if (formData.orderBy === undefined || formData.orderBy === null) {
                return false; // Insert after must be selected
            }
        }

        return true;
    }, [formData, editingId]);

    const handleSave = async () => {
        if (!dataService || !formData.bezeichnung) {
            setError('Please fill in required fields');
            return;
        }

        try {
            setIsLoading(true);

            // Helper function to convert Date to ISO date string (YYYY-MM-DD) for API
            const dateToISOString = (date: Date | null): string => {
                if (!date) {
                    return '0001-01-01';
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const submitData: ArbeitspaketItem = {
                paketID: formData.paketID,
                projektID: project?.projektID || 0,
                prefix: formData.prefix,
                bezeichnung: formData.bezeichnung,
                datumVon: dateToISOString(formData.startdatum) as any,
                datumBis: dateToISOString(formData.endedatum) as any,
                userFullName: formData.userFullName,
                userID: formData.userID,
                personalkostenPlan: formData.personalkostenPlan,
                parentID: formData.parentID ? parseInt(formData.parentID.toString(), 10) : undefined,
                orderBy: formData.orderBy || 0,
            } as any;

            if (editingId) {
                await dataService.updateArbeitspaket(submitData, formData.orderBy || 0);
            } else {
                await dataService.addArbeitspaket(submitData, formData.orderBy || 0);
            }

            setIsDrawerOpen(false);
            await fetchArbeitspakete();
        } catch (err) {
            console.error('Error saving work package:', err);
            setError('Error saving work package');
        } finally {
            setIsLoading(false);
        }
    };

    const renderRow = (item: ArbeitspaketItem, level: number = 0, isChild: boolean = false, isLastChild: boolean = false) => {
        const children = hierarchicalData.get(item.paketID) || [];
        const isCollapsed = collapsedGroups.has(item.paketID);
        const hasChildren = children.length > 0;

        const toggleCollapse = () => {
            const newSet = new Set(collapsedGroups);
            if (newSet.has(item.paketID)) {
                newSet.delete(item.paketID);
            } else {
                newSet.add(item.paketID);
            }
            setCollapsedGroups(newSet);
        };

        // Determine which tree connector to use: └─ for last child, ├─ for others
        const connector = isChild ? (isLastChild ? '└─' : '├─') : '';

        return (
            <React.Fragment key={item.paketID}>
                <TableRow
                    className={isChild ? styles.childRow : undefined}
                    draggable
                    onDragStart={(e: React.DragEvent<HTMLTableRowElement>) => handleDragStart(e, item)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e: React.DragEvent<HTMLTableRowElement>) => handleDragOver(e, item)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e: React.DragEvent<HTMLTableRowElement>) => handleDrop(e, item)}
                    style={{
                        cursor: 'grab',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                        // Visual feedback for before position
                        borderTop: dragOverItem?.paketID === item.paketID && dropPosition === 'before'
                            ? '5px solid #ff8c00'
                            : undefined,
                        // Visual feedback for after position
                        borderBottom: dragOverItem?.paketID === item.paketID && dropPosition === 'after'
                            ? '5px solid #107c10'
                            : undefined,
                        // Visual feedback for child position
                        backgroundColor: dragOverItem?.paketID === item.paketID && dropPosition === 'child'
                            ? '#10732620'
                            : undefined,
                        boxShadow: dragOverItem?.paketID === item.paketID && dropPosition === 'child'
                            ? 'inset 0 0 0 2px #107c10'
                            : undefined,
                        outline: dragOverItem?.paketID === item.paketID && dropPosition === 'child'
                            ? '2px solid #107c10'
                            : 'none',
                        outlineOffset: dragOverItem?.paketID === item.paketID && dropPosition === 'child'
                            ? '-2px'
                            : undefined,
                    }}
                    title={dragOverItem?.paketID === item.paketID
                        ? dropPosition === 'before'
                            ? '⬆ Insert before this item'
                            : dropPosition === 'after'
                                ? '⬇ Insert after this item'
                                : '📦 Make as child of this item'
                        : 'Drag to reorder or change parent'}
                >
                    <TableCell>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <span style={{
                                cursor: 'grab',
                                fontSize: '16px',
                                opacity: 0.6,
                                userSelect: 'none'
                            }}>⋮⋮</span>
                            {dragOverItem?.paketID === item.paketID && (
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: tokens.borderRadiusMedium,
                                    fontSize: tokens.fontSizeBase200,
                                    fontWeight: 600,
                                    backgroundColor: dropPosition === 'before'
                                        ? '#ff8c0030'
                                        : dropPosition === 'after'
                                            ? '#107c1030'
                                            : '#10732620',
                                    color: dropPosition === 'before'
                                        ? '#ff8c00'
                                        : dropPosition === 'after'
                                            ? '#107c10'
                                            : '#107326',
                                    marginRight: '4px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {dropPosition === 'before'
                                        ? '⬆ Before'
                                        : dropPosition === 'after'
                                            ? '⬇ After'
                                            : '📦 Child'}
                                </span>
                            )}
                            <Button
                                icon={<Eye24Regular />}
                                size="small"
                                onClick={() => handleView(item)}
                                title="View"
                            />
                            <Button
                                icon={<Edit24Regular />}
                                size="small"
                                onClick={() => handleEdit(item)}
                                title="Edit"
                            />
                        </div>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            {hasChildren && (
                                <Button
                                    icon={isCollapsed ? <ChevronRight24Regular /> : <ChevronDown24Regular />}
                                    size="small"
                                    onClick={toggleCollapse}
                                    title={isCollapsed ? 'Expand' : 'Collapse'}
                                />
                            )}
                        </TableCellLayout>
                        <TableCellLayout>

                        </TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: `${(level - 1) * 15}px`, flexShrink: 0 }} />
                                {connector && <span style={{ marginRight: '4px' }}>{connector}</span>}

                                <span>{item.prefixTitle}</span>
                            </div>
                        </TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>{item.bezeichnung}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            {item.datumVon ? new Date(item.datumVon).toLocaleDateString() : '-'}
                        </TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            {item.datumBis ? new Date(item.datumBis).toLocaleDateString() : '-'}
                        </TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>{item.userFullName || '-'}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            {item.personalkostenPlan ? `€${item.personalkostenPlan.toFixed(2)}` : '-'}
                        </TableCellLayout>
                    </TableCell>
                </TableRow>
                {!isCollapsed && children.map((child, index) => renderRow(child, level + 1, true, index === children.length - 1))}
            </React.Fragment>
        );
    };

    if (!project) return null;

    return (
        <div className={styles.container}>
            {/* Work Package Levels Section */}
            <div style={{
                marginBottom: tokens.spacingVerticalL,
                padding: tokens.spacingHorizontalM,
                backgroundColor: tokens.colorNeutralBackground2,
                borderRadius: tokens.borderRadiusMedium,
                maxWidth: '300px'
            }}>
                <h3 style={{ margin: `0 0 ${tokens.spacingVerticalM} 0` }}>Work Package Levels</h3>

                {isLoadingEbenen ? (
                    <Spinner label="Loading levels..." size="small" />
                ) : editedEbenen.length > 0 ? (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalL }}>
                            {editedEbenen.map((level, index) => (
                                <div key={`level-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                                    <label style={{ fontWeight: 600, fontSize: tokens.fontSizeBase200 }}>Prefix for Level {index + 1}</label>
                                    <Input
                                        value={level.prefix || ''}
                                        onChange={(_e, data) => {
                                            const newEbenen = [...editedEbenen];
                                            newEbenen[index] = { ...newEbenen[index], prefix: data.value };
                                            setEditedEbenen(newEbenen);
                                        }}
                                        placeholder="e.g., AP, TT"
                                    />
                                </div>
                            ))}
                        </div>

                        <Button
                            onClick={handleSaveEbenen}
                            disabled={isSavingEbenen || JSON.stringify(ebenen) === JSON.stringify(editedEbenen)}
                            appearance="primary"
                            style={{ width: '100%' }}
                        >
                            Save Levels
                        </Button>
                    </>
                ) : (
                    <p style={{ color: tokens.colorNeutralForeground3 }}>No work package levels configured</p>
                )}
            </div>

            <div className={styles.toolbar}>
                <Button
                    icon={<Add24Regular />}
                    onClick={handleAddNew}
                    appearance="primary"
                >
                    Add Work Package
                </Button>
                {isSavingDragDrop && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                        <Spinner size="small" />
                        <span style={{ fontSize: tokens.fontSizeBase300 }}>Updating order...</span>
                    </div>
                )}
                <div style={{ marginLeft: 'auto', fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 }}>
                    💡 Drag rows to reorder or change parent packages
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading work packages..." />
                </div>
            ) : arbeitspakete.length === 0 ? (
                <div className={styles.emptyState}>No work packages found</div>
            ) : (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions}>Actions</TableHeaderCell>
                                <TableHeaderCell className={styles.colExpand}></TableHeaderCell>

                                <TableHeaderCell className={styles.colPackageNumber}>Package Number</TableHeaderCell>
                                <TableHeaderCell>Description</TableHeaderCell>
                                <TableHeaderCell className={styles.colStartDate}>Start Date</TableHeaderCell>
                                <TableHeaderCell className={styles.colEndDate}>End Date</TableHeaderCell>
                                <TableHeaderCell>Responsibility</TableHeaderCell>
                                <TableHeaderCell>Personnel Costs (Plan)</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {topLevelPackages.map(item => renderRow(item))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Drawer
                open={isDrawerOpen}
                onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>
                        {isViewOnly ? 'View Work Package' : (editingId ? 'Edit Work Package' : 'Add Work Package')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {/* Work Package Properties Section */}
                        <div>
                            <div className={styles.sectionHeader}>Work Package Properties</div>
                            <div className={styles.sectionDescription}>
                                Work package number (automatically assigned), parent work package, description and other information
                            </div>

                            {/* Package Number (Read-only) */}
                            {(editingId || isViewOnly) && (<div className={styles.formGroup}>
                                <Label>Work Package Number</Label>
                                <div className={styles.readOnlyField}>
                                    {editingId ? (arbeitspakete.find(ap => ap.paketID === editingId)?.prefixTitle || '-') : '-'}
                                </div>
                            </div>)}

                            {/* Parent Package - show tree when creating new, select when viewing */}
                            {(!editingId || isViewOnly) && (
                                <div className={styles.formGroup}>
                                    <Label>Parent Work Package</Label>
                                    {!editingId && !isViewOnly ? (
                                        renderParentTree()
                                    ) : isViewOnly ? (
                                        // In view mode, display parent as read-only text
                                        <div style={{ padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                                            {formData.parentID ? (
                                                (() => {
                                                    const parentPackage = arbeitspakete.find(ap => ap.paketID === formData.parentID);
                                                    return parentPackage
                                                        ? `${parentPackage.prefixTitle}: ${parentPackage.bezeichnung}`
                                                        : 'Parent not found';
                                                })()
                                            ) : (
                                                'None (Top-level)'
                                            )}
                                        </div>
                                    ) : (
                                        <Select
                                            value={formData.parentID?.toString() || ''}
                                            disabled={isViewOnly}
                                            onChange={(_e, data) => {
                                                setFormData({
                                                    ...formData,
                                                    parentID: data.value ? parseInt(data.value, 10) : undefined,
                                                });
                                            }}
                                        >
                                            <option value="">None (Top-level)</option>
                                            {parentOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </div>
                            )}

                            {/* Description */}
                            <div className={styles.formGroup}>
                                <Label required>Description</Label>
                                <Input
                                    value={formData.bezeichnung}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, bezeichnung: data.value });
                                    }}
                                    placeholder="Enter description"
                                />
                            </div>

                            {/* Start Date */}
                            <div className={styles.formGroup}>
                                <Label>Start Date</Label>
                                <DatePicker
                                    value={formData.startdatum}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, startdatum: date || null });
                                    }}
                                    placeholder="Select start date"
                                />
                            </div>

                            {/* End Date */}
                            <div className={styles.formGroup}>
                                <Label>End Date</Label>
                                <DatePicker
                                    value={formData.endedatum}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, endedatum: date || null });
                                    }}
                                    placeholder="Select end date"
                                />
                            </div>

                            {/* Responsibility */}
                            <div className={styles.formGroup}>
                                <Label>Responsibility</Label>
                                {!isResponsibilitySearching && !formData.userID ? (
                                    <Button
                                        appearance="subtle"
                                        disabled={isViewOnly}
                                        onClick={() => {
                                            setIsResponsibilitySearching(true);
                                            setFormData({
                                                ...formData,
                                                userFullName: '', // Clear the "-" value when entering search mode
                                            });
                                            loadAvailableMitarbeiter();
                                        }}
                                        style={{
                                            justifyContent: 'flex-start',
                                            padding: tokens.spacingHorizontalM,
                                            backgroundColor: tokens.colorNeutralBackground2,
                                        }}
                                    >
                                        No Responsibility
                                    </Button>
                                ) : (
                                    <Combobox
                                        placeholder="Enter at least 2 characters..."
                                        disabled={isViewOnly}
                                        value={formData.userFullName || ''}
                                        autoFocus
                                        onOptionSelect={(_, option) => {
                                            const userID = parseInt(option.optionValue || '0');
                                            if (userID === 0) {
                                                // "No Responsibility" selected
                                                setFormData({
                                                    ...formData,
                                                    userID: undefined,
                                                    userFullName: '',
                                                });
                                                setIsResponsibilitySearching(false);
                                                setMemberSearchResults([]);
                                            } else {
                                                const member = memberSearchResults.find((m) => m.personalID === userID);
                                                if (member) {
                                                    setFormData({
                                                        ...formData,
                                                        userID: member.personalID,
                                                        userFullName: member.fullName || `${member.vorname} ${member.nachname}`,
                                                    });
                                                    setIsResponsibilitySearching(false);
                                                    setMemberSearchResults([]);
                                                }
                                            }
                                        }}
                                        onChange={(ev) => {
                                            const value = (ev.target as HTMLInputElement).value;
                                            setFormData({ ...formData, userFullName: value });
                                            handleResponsibilitySearch(value);
                                        }}
                                        onBlur={() => {
                                            // Reset to "No Responsibility" if no selection made
                                            if (!formData.userID) {
                                                setIsResponsibilitySearching(false);
                                                setMemberSearchResults([]);
                                                setFormData({
                                                    ...formData,
                                                    userFullName: '',
                                                });
                                            }
                                        }}
                                    >
                                        <Option value="0" text="No Responsibility">
                                            No Responsibility
                                        </Option>
                                        {memberSearchResults.map((member) => (
                                            <Option
                                                key={member.personalID}
                                                value={member.personalID.toString()}
                                                text={member.fullName || `${member.vorname} ${member.nachname}`}
                                            >
                                                {member.fullName || `${member.vorname} ${member.nachname}`}
                                            </Option>
                                        ))}
                                    </Combobox>
                                )}
                            </div>

                            {/* Personnel Costs */}
                            <div className={styles.formGroup}>
                                <Label required>Personnel Costs (Plan)</Label>
                                <Input
                                    type="number"
                                    value={formData.personalkostenPlan?.toString() || ''}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        const numValue = data.value ? parseFloat(data.value) : 0;
                                        setFormData({
                                            ...formData,
                                            personalkostenPlan: isNaN(numValue) ? 0 : numValue,
                                        });
                                    }}
                                    placeholder="0.00"
                                />
                            </div>

                            {/* Insert After - only show when not viewing */}
                            {!isViewOnly && (
                                <div className={styles.formGroup}>
                                    <Label required>Insert After</Label>
                                    <Select
                                        value={formData.orderBy?.toString() || ''}
                                        onChange={(_e, data) => {
                                            setFormData({
                                                ...formData,
                                                orderBy: data.value ? parseInt(data.value, 10) : 0,
                                            });
                                        }}
                                    >
                                        {insertAfterOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Milestone Properties Section */}
                        {!isViewOnly && (
                            <div>
                                <div className={styles.sectionHeader}>Milestone Properties</div>
                                <div className={styles.sectionDescription}>
                                    Description and date if a milestone is linked or should be created
                                </div>

                                {/* Create Milestone */}
                                <div className={styles.formGroup}>
                                    <Label required>Create Milestone</Label>
                                    <RadioGroup
                                        layout="horizontal"
                                        value={formData.createMilestone === true ? 'yes' : 'no'}
                                        onChange={(_e, data) => {
                                            setFormData({ ...formData, createMilestone: data.value === 'yes' });
                                        }}
                                        disabled={isViewOnly}
                                    >
                                        <Radio value="yes" label="Yes" />
                                        <Radio value="no" label="No" />
                                    </RadioGroup>
                                </div>
                            </div>
                        )}
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    <Button
                        appearance="secondary"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        {isViewOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!isViewOnly && (
                        <Button
                            appearance="primary"
                            onClick={handleSave}
                            disabled={!isFormValid}
                        >
                            Save
                        </Button>
                    )}
                </DrawerFooter>
            </Drawer>
        </div>
    );
};

export default ProjektmanagementArbeitspakete;
